import { hasVerifiedBadge, VerifiedBadge } from '@/components/badges/VerifiedBadge'
import { AddCircle, Copy, ExpandMore, MoreVertical, PickleBall, UserIconFilled } from '@/components/icons'
import { FollowingModal } from '@/components/modal'
import { ROUTE_NEW_MATCH, ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useFollow, useMyBadges, useUnfollow } from '@/services'
import { Player, User } from '@/types'
import { FollowingInfo } from '@/types/Activity'
import { Menu, Transition } from '@headlessui/react'
import moment from 'moment'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { useCopyToClipboard } from 'usehooks-ts'
import { ShareButton } from '../button'

type Props = {
  data: User | Player
  openChat?: () => void
  followingInfo: FollowingInfo
  playerId?: string
  refetchFollowingInfo?: () => void
}

export const Profile = ({ data, openChat, followingInfo, playerId, refetchFollowingInfo }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAppSelector(userSelector)

  // Player payloads carry badges; the redux User (own dashboard) does not —
  // fall back to the own-badges endpoint there.
  const isOwnUser = !!user && data?.id === user.id
  const myBadges = useMyBadges(isOwnUser)
  const badges = data && 'badges' in data && data.badges ? data.badges : isOwnUser ? myBadges : undefined

  const [value, copy] = useCopyToClipboard()
  const [openFollowingModal, setOpenFollowingModal] = useState<'FOLLOWING' | 'FOLLOWERS' | ''>('')
  const [info, setInfo] = useState<FollowingInfo>({
    followers: 0,
    followings: 0,
    isFollowed: false,
  })

  const { mutate: follow } = useFollow()
  const { mutate: unfollow } = useUnfollow()

  const shareUrl = playerId ? window.location.href : `${window.location.origin}${ROUTE_PLAYER_PROFILE}/${user?.id}`
  const shareText = `Hey, have a look at ${data?.fullName}'s profile on DUPR`

  const onCloseFollowingModal = () => setOpenFollowingModal('')

  const navigateCreateMatch = () => navigate(ROUTE_NEW_MATCH)

  const onCopyClipboard = () => {
    if ('duprId' in data && data?.duprId) copy(data.duprId)
    if ('referralCode' in data && data?.referralCode) copy(data.referralCode)
  }

  const handleFollow = (type: 'FOLLOW' | 'UN_FOLLOW') => {
    if (type === 'FOLLOW')
      follow(data?.id, {
        onSuccess: () => {
          setInfo({
            ...info,
            followers: info.followers + 1,
            isFollowed: true,
          })
        },
      })
    if (type === 'UN_FOLLOW')
      unfollow(data?.id, {
        onSuccess: () => {
          setInfo({
            ...info,
            followers: info.followers - 1,
            isFollowed: false,
          })
        },
      })
  }

  useEffect(() => {
    setInfo(followingInfo)
  }, [followingInfo])

  return (
    <div className="flex flex-col gap-y-3 md:gap-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-y-2 md:gap-x-6">
        <div className="flex items-center gap-x-4 md:gap-x-6">
          {data?.imageUrl ? (
            <img src={data.imageUrl} alt="avatar" className="w-16 h-16 md:w-24 md:h-24 rounded-full" />
          ) : (
            <UserIconFilled className="w-16 h-16 md:w-24 md:h-24 text-white" />
          )}
          {data && (
            <div className="flex flex-col font-semibold w-64">
              <span className="flex items-center gap-x-1.5 text-xl text-white">
                {hasVerifiedBadge(badges) && <VerifiedBadge />}
                {data?.fullName}
              </span>
              <span className="text-white text-opacity-50 text-sm">
                {data?.gender ? data?.gender[0] : ''}
                {'birthdate' in data
                  ? ` • ${moment().diff(data?.birthdate, 'years')}`
                  : 'age' in data
                    ? ` • ${data.age}`
                    : ''}
                {'addresses' in data && data?.addresses?.length
                  ? ` • ${data?.addresses?.[0]?.shortAddress}`
                  : 'shortAddress' in data
                    ? ` • ${data.shortAddress}`
                    : ''}
              </span>
            </div>
          )}
        </div>
        {user && (
          <div className="flex justify-between md:justify-start md:gap-x-10">
            <div
              className="flex w-full flex-col md:flex-row items-center gap-x-2 cursor-pointer"
              onClick={() => setOpenFollowingModal('FOLLOWING')}
            >
              <p className="text-white md:text-[32px] md:leading-10 font-bold">
                {info?.followings
                  ? info?.followings >= 1000000
                    ? `${(info?.followings / 1000000).toFixed(1)}M`
                    : info?.followings >= 1000
                      ? `${(info?.followings / 1000).toFixed(1)}k`
                      : info?.followings
                  : 0}
              </p>
              <p className="text-white text-xs">{t('common.following')}</p>
            </div>
            <div
              className="flex w-full flex-col md:flex-row items-center gap-x-2 cursor-pointer"
              onClick={() => setOpenFollowingModal('FOLLOWERS')}
            >
              <p className="text-white md:text-[32px] md:leading-10 font-bold">
                {info?.followers
                  ? info?.followers >= 1000000
                    ? `${(info?.followers / 1000000).toFixed(1)}M`
                    : info?.followers >= 1000
                      ? `${(info?.followers / 1000).toFixed(1)}k`
                      : info?.followers
                  : 0}
              </p>
              <p className="text-white text-xs">{t('common.followers')}</p>
            </div>
          </div>
        )}
      </div>
      {!openChat && user && (
        <div className="flex items-center justify-between md:justify-start gap-x-4">
          <div className="flex items-center gap-x-4">
            <ShareButton
              shareUrl={shareUrl}
              shareText={shareText}
              pageName={TRACKING_TYPES.USER_PROFILE}
              shareContent={TRACKING_TYPES.PROFILE_SHARE}
            />
            <div className="relative">
              <button
                className="flex items-center rounded-full gap-x-1 text-sm text-white text-opacity-50 font-medium player-dupr-id"
                onClick={onCopyClipboard}
              >
                ID: {'duprId' in data ? data?.duprId : 'referralCode' in data ? data?.referralCode : ''}
                <Copy className="text-white text-opacity-40" />
              </button>
              <Tooltip anchorSelect=".player-dupr-id" place="bottom" className="text-navy-900 z-50">
                {value ? t('common.DUPRIDCopied') : t('common.copyClipboard')}
              </Tooltip>
            </div>
          </div>

          <Menu as="div" className="relative inline-block">
            <Menu.Button className="rounded-full p-2 bg-white bg-opacity-10">
              <AddCircle className="text-white w-5 h-5" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute z-50 right-0 mt-2 w-32 bg-white rounded-2xl py-2 shadow-xl">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`${
                        active ? 'bg-slate-300 text-navy-900' : ''
                      } font-semibold flex w-full items-center px-3 py-2 gap-x-2 rounded-md`}
                      onClick={navigateCreateMatch}
                    >
                      <PickleBall />
                      {t('common.match')}
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      )}
      {openChat && user && user?.id !== Number(playerId) && (
        <div className="flex items-center gap-x-1">
          {info?.isFollowed ? (
            <Menu as="div" className="relative inline-block">
              <Menu.Button className="flex flex-1 md:flex-none gap-x-1 items-center justify-center bg-white bg-opacity-20 rounded-full text-sm text-white font-semibold w-36 h-9">
                {t('common.following')}
                <ExpandMore className="text-white" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute z-50 mt-2 w-36 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="px-1 py-1 ">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active
                              ? ' bg-slate-300 text-navy-900 font-semibold'
                              : info?.isFollowed
                                ? 'text-gray-500'
                                : 'text-navy-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm gap-x-1`}
                          disabled={followingInfo?.isFollowed}
                        >
                          {t('common.following')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active
                              ? ' bg-slate-300 text-navy-900 font-semibold'
                              : !info?.isFollowed
                                ? 'text-gray-500'
                                : 'text-navy-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          onClick={() => handleFollow('UN_FOLLOW')}
                        >
                          {t('common.unFollow')}
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          ) : (
            <button
              className="flex flex-1 md:flex-none gap-x-1 items-center justify-center bg-white bg-opacity-20 rounded-full text-sm text-white font-semibold w-36 h-9"
              onClick={() => handleFollow('FOLLOW')}
            >
              {t('common.follow')}
            </button>
          )}

          {/* Chat is temporarily disabled: the profile "Message" button has been removed. */}
          <Menu as="div" className="relative inline-block">
            <Menu.Button className="w-9 h-9 rounded-full flex items-center justify-center  bg-white bg-opacity-[0.08]">
              <MoreVertical className="text-white" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute z-50 right-0 mt-2 w-40 bg-white rounded py-1 origin-top-right divide-y divide-gray-300 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <Menu.Item>
                  {() => {
                    return (
                      <div>
                        <ShareButton
                          className={`!text-navy-900 !w-full !justify-start !rounded !hover:bg-slate-300`}
                          iconClass="!text-navy-900"
                          shareUrl={shareUrl}
                          shareText={shareText}
                          pageName={TRACKING_TYPES.USER_PROFILE}
                          shareContent={TRACKING_TYPES.PROFILE_SHARE}
                        />
                      </div>
                    )
                  }}
                </Menu.Item>
                <Menu.Item>
                  {() => (
                    <div className="relative">
                      <button
                        className={`flex items-center gap-x-4 px-4 py-2 text-sm font-semibold text-navy-900 w-full player-dupr-id `}
                        onClick={onCopyClipboard}
                      >
                        ID: {'duprId' in data ? data?.duprId : 'referralCode' in data ? data?.referralCode : ''}
                        <Copy className="text-navy-900" />
                      </button>
                      <Tooltip anchorSelect=".player-dupr-id" place="bottom" className="text-navy-900 z-50">
                        {t('common.copyClipboard')}
                      </Tooltip>
                    </div>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      )}
      <FollowingModal
        open={openFollowingModal}
        onClose={onCloseFollowingModal}
        playerId={data?.id}
        followingInfo={info}
        refetchFollowingInfo={refetchFollowingInfo}
      />
    </div>
  )
}
